import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Shield, Award, Target } from 'lucide-react';
import  qualityImage  from '../assets/q.png'
import Injections1 from '../assets/Injections1.png'
import infusion1 from '../assets/infusion1.png'
import oncology1 from '../assets/image3.png'

const useReveal = (delay = 0) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setTimeout(() => setVis(true), delay); obs.disconnect(); }
    }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
};

const QualityPage = () => {
  const { t } = useTranslation();

  const [introLeftRef,  introLeftVis]  = useReveal(0);
  const [introRightRef, introRightVis] = useReveal(160);
  const [philLeftRef,   philLeftVis]   = useReveal(0);
  const [philRightRef,  philRightVis]  = useReveal(160);
  const [qcLeftRef,     qcLeftVis]     = useReveal(0);
  const [qcRightRef,    qcRightVis]    = useReveal(160);
  const [ctaRef,        ctaVis]        = useReveal(0);

  const qcPoints = t('qualityPage.qc.points', { returnObjects: true }) || [];

  return (
    <div className="qp-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Playfair+Display:wght@700;800&display=swap');

        /* ── Animations ── */
        @keyframes qpHeroIn   { from{opacity:0;transform:translateY(36px)} to{opacity:1;transform:translateY(0)} }
        @keyframes qpSlideL   { from{opacity:0;transform:translateX(-44px)} to{opacity:1;transform:translateX(0)} }
        @keyframes qpSlideR   { from{opacity:0;transform:translateX(44px)}  to{opacity:1;transform:translateX(0)} }
        @keyframes qpUp       { from{opacity:0;transform:translateY(32px)}  to{opacity:1;transform:translateY(0)} }
        @keyframes qpPulse    { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.5);opacity:.4} }

        .qp-root { font-family:'Outfit',sans-serif; }

        /* ══════════════════════════════
           1. HERO
        ══════════════════════════════ */
        .qp-hero {
          position:relative; min-height:260px;
          display:flex; align-items:center;
          overflow:hidden;
          background:linear-gradient(135deg,#0d2233 0%,#1a3a4a 55%,#0f2d3d 100%);
          padding-top:80px;
        }
        .qp-hero-bg {
          position:absolute; inset:0; width:100%; height:100%;
          object-fit:cover; opacity:0.18;
        }
        .qp-hero-grid {
          position:absolute; inset:0; pointer-events:none;
          background-image:
            linear-gradient(rgba(58,143,163,0.07) 1px,transparent 1px),
            linear-gradient(90deg,rgba(58,143,163,0.07) 1px,transparent 1px);
          background-size:56px 56px;
        }
        .qp-hero-line {
          position:absolute; top:0; left:0; right:0; height:3px;
          background:linear-gradient(90deg,transparent,#3a8fa3,#5aab9a,#3a8fa3,transparent);
        }
        .qp-hero-inner {
          position:relative; z-index:2;
          max-width:1280px; margin:0 auto; padding:60px 32px 56px;
          display:grid; grid-template-columns:1fr 1fr; gap:40px; align-items:center;
          width:100%;
        }
        @media(max-width:768px){ .qp-hero-inner{grid-template-columns:1fr;} }

        .qp-hero-left { animation:qpHeroIn 0.85s cubic-bezier(0.4,0,0.2,1) 0.1s both; }
        .qp-hero-eyebrow {
          display:inline-block; font-size:0.72rem; font-weight:600;
          letter-spacing:0.14em; text-transform:uppercase;
          color:#5aab9a; background:rgba(90,171,154,0.12);
          border:1px solid rgba(90,171,154,0.3);
          padding:5px 16px; border-radius:999px; margin-bottom:16px;
        }
        .qp-hero-title {
          font-family:'Playfair Display',serif;
          font-size:clamp(2.4rem,5vw,3.5rem); font-weight:800;
          color:#fff; margin:0; line-height:1.1;
          text-shadow:0 2px 24px rgba(0,0,0,0.3);
        }
        .qp-hero-right {
          animation:qpHeroIn 0.85s cubic-bezier(0.4,0,0.2,1) 0.25s both;
          text-align:right;
        }
        .qp-hero-tag {
          font-family:'Playfair Display',serif;
          font-size:clamp(1.6rem,3vw,2.6rem); font-weight:800;
          color:#3a8fa3; line-height:1.2;
        }
        .qp-hero-sub {
          font-size:0.8rem; font-weight:500; letter-spacing:0.1em;
          color:rgba(255,255,255,0.35); text-transform:uppercase; margin-top:6px;
        }

        /* ══════════════════════════════
           Shared section bg
        ══════════════════════════════ */
        .qp-teal-bg {
          background:linear-gradient(180deg,#e8f5f7 0%,#d4eef2 60%,#e0f4f7 100%);
          position:relative; overflow:hidden;
        }
        .qp-teal-bg-2 { background:linear-gradient(180deg,#d4eef2 0%,#c8e8ed 100%); position:relative; overflow:hidden; }
        .qp-dots {
          position:absolute; inset:0; pointer-events:none;
          background-image:radial-gradient(circle,rgba(58,143,163,0.06) 1px,transparent 1px);
          background-size:36px 36px;
        }
        .qp-blob {
          position:absolute; border-radius:50%; pointer-events:none; filter:blur(80px);
        }
        .qp-blob-tl { width:400px;height:400px;background:rgba(58,143,163,0.08);top:-100px;left:-100px; }
        .qp-blob-br { width:350px;height:350px;background:rgba(90,171,154,0.07);bottom:-80px;right:-80px; }

        .qp-inner { max-width:1280px; margin:0 auto; padding:0 32px; position:relative; z-index:1; }

        /* ══════════════════════════════
           2. INTRO (pill images + text)
        ══════════════════════════════ */
        .qp-intro { padding:90px 0 80px; }
        .qp-intro-grid {
          display:grid; grid-template-columns:1fr 1fr; gap:70px; align-items:center;
        }
        @media(max-width:900px){ .qp-intro-grid{grid-template-columns:1fr;} }

        .qp-img-pair {
          display:flex; gap:18px; align-items:flex-end;
          opacity:0; transform:translateX(-44px);
          transition:all 0.85s cubic-bezier(0.4,0,0.2,1);
        }
        .qp-img-pair.in { opacity:1; transform:translateX(0); }

        /* Pill shape: top two rounded corners + tall */
        .qp-pill {
          flex:1; overflow:hidden;
          border:2.5px solid rgba(255,255,255,0.7);
          box-shadow:0 16px 48px rgba(58,143,163,0.18),0 4px 16px rgba(0,0,0,0.08);
          transition:transform 0.4s cubic-bezier(0.4,0,0.2,1),box-shadow 0.4s;
        }
        .qp-pill:first-child {
          height:340px;
          border-radius:180px 180px 120px 120px;
          margin-bottom:0;
        }
        .qp-pill:last-child {
          height:340px;
          border-radius:180px 180px 120px 120px;
          margin-bottom:0;
        }
        .qp-pill:hover {
          transform:translateY(-8px);
          box-shadow:0 28px 64px rgba(58,143,163,0.26),0 8px 24px rgba(0,0,0,0.1);
        }
        .qp-pill img { width:100%; height:100%; object-fit:cover; display:block; }

        .qp-intro-text {
          opacity:0; transform:translateX(44px);
          transition:all 0.85s cubic-bezier(0.4,0,0.2,1) 0.15s;
        }
        .qp-intro-text.in { opacity:1; transform:translateX(0); }

        .qp-section-title {
          font-family:'Playfair Display',serif;
          font-size:clamp(1.8rem,3vw,2.6rem); font-weight:800;
          color:#1a3a4a; margin:0 0 8px;
        }
        .qp-section-sub {
          font-size:1rem; font-weight:500; color:#3a8fa3; margin-bottom:22px;
        }
        .qp-body {
          font-size:1rem; color:#3d5a6a; line-height:1.9;
          text-align:justify; margin-bottom:16px;
        }

        /* ══════════════════════════════
           3. PHILOSOPHY (text left, img right)
        ══════════════════════════════ */
        .qp-phil { padding:80px 0; }
        .qp-phil-grid {
          display:grid; grid-template-columns:1fr 1fr; gap:70px; align-items:center;
        }
        @media(max-width:900px){ .qp-phil-grid{grid-template-columns:1fr;} }

        .qp-slide-l { opacity:0; transform:translateX(-44px); transition:all 0.85s cubic-bezier(0.4,0,0.2,1); }
        .qp-slide-r { opacity:0; transform:translateX(44px);  transition:all 0.85s cubic-bezier(0.4,0,0.2,1) 0.15s; }
        .qp-slide-l.in { opacity:1; transform:translateX(0); }
        .qp-slide-r.in { opacity:1; transform:translateX(0); }

        .qp-img-frame {
          border-radius:20px; overflow:hidden;
          box-shadow:0 20px 56px rgba(58,143,163,0.18),0 6px 20px rgba(0,0,0,0.08);
          transition:transform 0.4s cubic-bezier(0.4,0,0.2,1),box-shadow 0.4s;
        }
        .qp-img-frame:hover {
          transform:translateY(-8px);
          box-shadow:0 32px 72px rgba(58,143,163,0.24),0 10px 28px rgba(0,0,0,0.1);
        }
        .qp-img-frame img { width:100%; height:300px; object-fit:cover; display:block; }

        /* ══════════════════════════════
           4. QC (3 pill images + bullet list)
        ══════════════════════════════ */
        .qp-qc { padding:80px 0 90px; }
        .qp-qc-grid {
          display:grid; grid-template-columns:1fr 1fr; gap:70px; align-items:center;
        }
        @media(max-width:900px){ .qp-qc-grid{grid-template-columns:1fr;} }

        /* Three tall pill images side by side */
        .qp-triple {
          display:flex; gap:14px; align-items:flex-end;
          opacity:0; transform:translateX(-44px);
          transition:all 0.85s cubic-bezier(0.4,0,0.2,1);
        }
        .qp-triple.in { opacity:1; transform:translateX(0); }

        .qp-tall-pill {
          flex:1; overflow:hidden;
          border:2px solid rgba(255,255,255,0.65);
          box-shadow:0 12px 36px rgba(58,143,163,0.16),0 4px 12px rgba(0,0,0,0.07);
          transition:transform 0.4s cubic-bezier(0.4,0,0.2,1),box-shadow 0.4s;
          border-radius:999px 999px 120px 120px;
        }
        .qp-tall-pill:nth-child(1) { height:320px; margin-bottom:0; }
        .qp-tall-pill:nth-child(2) { height:380px; }
        .qp-tall-pill:nth-child(3) { height:320px; margin-bottom:0; }
        .qp-tall-pill:hover {
          transform:translateY(-8px);
          box-shadow:0 24px 56px rgba(58,143,163,0.24),0 6px 18px rgba(0,0,0,0.09);
        }
        .qp-tall-pill img { width:100%; height:100%; object-fit:cover; display:block; }

        /* Bullet list */
        .qp-qc-text {
          opacity:0; transform:translateX(44px);
          transition:all 0.85s cubic-bezier(0.4,0,0.2,1) 0.15s;
        }
        .qp-qc-text.in { opacity:1; transform:translateX(0); }

        .qp-point {
          display:flex; gap:14px; align-items:flex-start; margin-bottom:22px;
          padding:18px 20px; border-radius:16px;
          background:rgba(255,255,255,0.65);
          backdrop-filter:blur(16px) saturate(1.4);
          -webkit-backdrop-filter:blur(16px) saturate(1.4);
          border:1px solid rgba(255,255,255,0.88);
          box-shadow:0 2px 12px rgba(0,0,0,0.05),inset 0 1px 0 rgba(255,255,255,0.9);
          transition:all 0.3s cubic-bezier(0.4,0,0.2,1);
          position:relative; overflow:hidden;
        }
        .qp-point::before {
          content:''; position:absolute; left:0; top:0; bottom:0; width:3px;
          background:linear-gradient(180deg,#3a8fa3,#5aab9a);
          opacity:0; transition:opacity 0.3s;
        }
        .qp-point:hover {
          background:rgba(255,255,255,0.9);
          box-shadow:0 8px 28px rgba(58,143,163,0.14);
          border-color:rgba(58,143,163,0.22);
          transform:translateX(6px);
        }
        .qp-point:hover::before { opacity:1; }
        .qp-point-dot {
          width:8px; height:8px; border-radius:50%; flex-shrink:0; margin-top:7px;
          background:linear-gradient(135deg,#3a8fa3,#5aab9a);
          box-shadow:0 0 6px rgba(58,143,163,0.4);
        }
        .qp-point-title { font-size:0.95rem; font-weight:700; color:#1a3a4a; margin-bottom:4px; }
        .qp-point-body  { font-size:0.875rem; color:#3d5a6a; line-height:1.7; }

        /* ══════════════════════════════
           5. CTA BANNER (dark blue rounded card)
        ══════════════════════════════ */
        .qp-cta-wrap {
          padding:60px 32px 80px;
          background:linear-gradient(180deg,#c8e8ed 0%,#e0f4f7 100%);
        }
        .qp-cta {
          max-width:1200px; margin:0 auto;
          border-radius:32px; overflow:hidden;
          position:relative;
          background:linear-gradient(135deg,#0d2233 0%,#1a3a4a 60%,#0f2d3d 100%);
          display:grid; grid-template-columns:1.2fr 0.8fr; min-height:280px;
          opacity:0; transform:translateY(36px);
          transition:all 0.85s cubic-bezier(0.4,0,0.2,1);
        }
        @media(max-width:768px){ .qp-cta{grid-template-columns:1fr;} }
        .qp-cta.in { opacity:1; transform:translateY(0); }

        /* Grid overlay */
        .qp-cta-grid {
          position:absolute; inset:0; pointer-events:none;
          background-image:
            linear-gradient(rgba(58,143,163,0.07) 1px,transparent 1px),
            linear-gradient(90deg,rgba(58,143,163,0.07) 1px,transparent 1px);
          background-size:48px 48px;
        }
        /* Top accent line */
        .qp-cta-line {
          position:absolute; top:0; left:0; right:0; height:3px;
          background:linear-gradient(90deg,transparent,#3a8fa3,#5aab9a,#3a8fa3,transparent);
        }

        .qp-cta-left {
          position:relative; z-index:2; padding:52px 48px;
        }
        .qp-cta-title {
          font-family:'Playfair Display',serif;
          font-size:clamp(1.6rem,3vw,2.2rem); font-weight:800;
          color:#fff; margin:0 0 20px; line-height:1.25;
        }
        .qp-cta-body { font-size:0.95rem; color:rgba(255,255,255,0.68); line-height:1.85; margin-bottom:16px; }

        /* Right side — large decorative question mark glow */
        .qp-cta-right {
          position:relative; z-index:2;
          display:flex; align-items:center; justify-content:center;
          padding:40px 32px;
        }
        .qp-cta-deco {
          font-size:14rem; font-weight:900; line-height:1;
          background:linear-gradient(135deg,rgba(58,143,163,0.35),rgba(90,171,154,0.25));
          -webkit-background-clip:text; -webkit-text-fill-color:transparent;
          background-clip:text;
          filter:drop-shadow(0 0 40px rgba(58,143,163,0.4));
          user-select:none;
          font-family:'Playfair Display',serif;
        }
        /* Glow orbs */
        .qp-cta-orb1 {
          position:absolute; width:300px; height:300px; border-radius:50%;
          background:radial-gradient(circle,rgba(58,143,163,0.18) 0%,transparent 70%);
          top:-60px; right:-60px; pointer-events:none;
        }
        .qp-cta-orb2 {
          position:absolute; width:200px; height:200px; border-radius:50%;
          background:radial-gradient(circle,rgba(90,171,154,0.15) 0%,transparent 70%);
          bottom:-40px; left:-40px; pointer-events:none;
        }
      `}</style>

      {/* ══ 1. HERO ══ */}
      <section className="qp-hero">
        <img src={qualityImage} alt="" className="qp-hero-bg" />
        <div className="qp-hero-grid" />
        <div className="qp-hero-line" />
        <div className="qp-hero-inner">
          <div className="qp-hero-left">
            <div className="qp-hero-eyebrow">{t('qualityPage.hero.eyebrow')}</div>
            <h1 className="qp-hero-title">{t('qualityPage.hero.title')}</h1>
          </div>
          <div className="qp-hero-right">
            <div className="qp-hero-tag">{t('qualityPage.hero.tag').split('\n').map((line, i) => (<span key={i}>{line}{i === 0 && <br />}</span>))}</div>
            {/* <div className="qp-hero-sub">{t('qualityPage.hero.sub')}</div> */}
          </div>
        </div>
      </section>

      {/* ══ 2. INTRO — pill images + text ══ */}
      <section className="qp-teal-bg qp-intro">
        <div className="qp-dots" /><div className="qp-blob qp-blob-tl" /><div className="qp-blob qp-blob-br" />
        <div className="qp-inner">
          <div className="qp-intro-grid">
            {/* Pill images */}
            <div ref={introLeftRef} className={`qp-img-pair ${introLeftVis ? 'in' : ''}`}>
              <div className="qp-pill">
                <img src={Injections1} alt="Injections" />
              </div>
              <div className="qp-pill">
                <img src={infusion1} alt="Infusions" />
              </div>
            </div>
            {/* Text */}
            <div ref={introRightRef} className={`qp-intro-text ${introRightVis ? 'in' : ''}`}>
              <h2 className="qp-section-title">{t('qualityPage.intro.title')}</h2>
              <p className="qp-section-sub">{t('qualityPage.intro.subtitle')}</p>
              <p className="qp-body">{t('qualityPage.intro.p1')}</p>
              {/* <p className="qp-body">{t('qualityPage.intro.p2')}</p> */}
            </div>
          </div>
        </div>
      </section>

      {/* ══ 3. PHILOSOPHY — text left, image right ══ */}
      <section className="qp-teal-bg-2 qp-phil">
        <div className="qp-dots" /><div className="qp-blob qp-blob-br" />
        <div className="qp-inner">
          <div className="qp-phil-grid">
            <div ref={philLeftRef} className={`qp-slide-l ${philLeftVis ? 'in' : ''}`}>
              <h2 className="qp-section-title">{t('qualityPage.philosophy.title')}</h2>
              <p className="qp-body" style={{ marginTop: '18px' }}>{t('qualityPage.philosophy.body')}</p>
            </div>
            <div ref={philRightRef} className={`qp-slide-r ${philRightVis ? 'in' : ''}`}>
              <div className="qp-img-frame">
                <img src={qualityImage} alt="Quality Philosophy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ 4. QC — three tall pills left, bullet list right ══ */}
      <section className="qp-teal-bg qp-qc">
        <div className="qp-dots" /><div className="qp-blob qp-blob-tl" />
        <div className="qp-inner">
          <div className="qp-qc-grid">
            {/* Three tall pill images */}
            <div ref={qcLeftRef} className={`qp-triple ${qcLeftVis ? 'in' : ''}`}>
              {[
                Injections1,
                infusion1,
                oncology1,
              ].map((src, i) => (
                <div key={i} className="qp-tall-pill" style={{ transitionDelay: `${i * 80}ms` }}>
                  <img src={src} alt={`QC ${i + 1}`} />
                </div>
              ))}
            </div>

            {/* Bullet points */}
            <div ref={qcRightRef} className={`qp-qc-text ${qcRightVis ? 'in' : ''}`}>
              <h2 className="qp-section-title" style={{ marginBottom: '8px' }}>{t('qualityPage.qc.title')}</h2>
              <p className="qp-body" style={{ marginBottom: '24px' }}>{t('qualityPage.qc.intro')}</p>
              {qcPoints.map((pt, i) => (
                <div key={i} className="qp-point" style={{ transitionDelay: `${i * 80}ms` }}>
                  <div className="qp-point-dot" />
                  <div>
                    <div className="qp-point-title">{pt.title}:</div>
                    <div className="qp-point-body">{pt.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ 5. CTA DARK BANNER ══ */}
      <div className="qp-cta-wrap">
        <div ref={ctaRef} className={`qp-cta ${ctaVis ? 'in' : ''}`}>
          <div className="qp-cta-grid" />
          <div className="qp-cta-line" />
          <div className="qp-cta-orb1" /><div className="qp-cta-orb2" />

          <div className="qp-cta-left">
            <h2 className="qp-cta-title">{t('qualityPage.cta.title')}</h2>
            <p className="qp-cta-body">{t('qualityPage.cta.p1')}</p>
            <p className="qp-cta-body">{t('qualityPage.cta.p2')}</p>
          </div>

          <div className="qp-cta-right">
            <div className="qp-cta-deco">?</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QualityPage;